/**
 * How a single-slice operation names its slice: `'view'`, the plane of the
 * one view it was asked from; `'acquisition'`, the edit target's acquired
 * slice, which any tile of the target can show.
 */
export type SliceAddress = 'view' | 'acquisition';

/**
 * The single-slice operation a refusal speaks for, by the whole-extent
 * alternative it offers when no tile shows the acquired slice: clear the
 * whole structure, interpolate every empty slice, or smooth every slice.
 */
export type SliceRefusalRemedy =
    | 'clearStructure'
    | 'interpolateAllEmpty'
    | 'smoothAllSlices';

/** Why a single-slice operation has no slice of the edit target to act on. */
export interface SliceRefusalCause {
    /** The edit target is sampled obliquely: no view of it resolves to an image slice. */
    tilted: boolean;
    /** How the operation named the slice it cannot reach. */
    addressedBy: SliceAddress;
    /**
     * The operation the refusal speaks for, and so the action a tilt
     * refusal names, where the operation has one, and the alternative an
     * acquisition-plane refusal offers; clearing the whole structure when
     * absent.
     */
    remedy?: SliceRefusalRemedy;
}

/**
 * The remedies with tilt words of their own. Interpolation is not offered
 * on a tilted series, so it has none: its refusal there names the
 * acquisition plane, which no tile shows on a tilted image.
 */
type TiltRefusalRemedy = Exclude<SliceRefusalRemedy, 'interpolateAllEmpty'>;

const OBLIQUE_IMAGE = {
    clearStructure: 'postProcessing.delete.obliqueThisSlice',
    smoothAllSlices: 'postProcessing.smooth.obliqueThisSlice',
} as const satisfies Record<TiltRefusalRemedy, string>;

const ACQUISITION_PLANE_NOT_SHOWN = {
    clearStructure: 'postProcessing.delete.acquisitionPlaneNotShown',
    interpolateAllEmpty: 'interpolation.preview.acquisitionPlaneNotShown',
    smoothAllSlices: 'postProcessing.smooth.acquisitionPlaneNotShown',
} as const satisfies Record<SliceRefusalRemedy, string>;

export type SliceRefusalMessageId =
    | (typeof OBLIQUE_IMAGE)[TiltRefusalRemedy]
    | 'postProcessing.delete.thisSliceUnavailable'
    | (typeof ACQUISITION_PLANE_NOT_SHOWN)[SliceRefusalRemedy];

/**
 * The message id stating why a single-slice operation has no slice to act
 * on, by remedy. A tilted image comes first: no view of it resolves to an
 * image slice, so turning the view cannot help, and the words name the
 * operation's own action. Otherwise, and for an operation not offered on a
 * tilted image, the words follow how the slice was named: the view it was
 * asked from is off an image slice, or no tile shows the image in its
 * acquisition plane, and then the words name the operation's own
 * whole-extent alternative.
 */
export function sliceRefusalMessageId({
    tilted,
    addressedBy,
    remedy = 'clearStructure',
}: SliceRefusalCause): SliceRefusalMessageId {
    if (tilted && remedy !== 'interpolateAllEmpty') {
        return OBLIQUE_IMAGE[remedy];
    }
    return addressedBy === 'view'
        ? 'postProcessing.delete.thisSliceUnavailable'
        : ACQUISITION_PLANE_NOT_SHOWN[remedy];
}
